import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { pageTitle } from '@/utils/pageTitle';
import {
  Breadcrumbs,
  Box,
  Button,
  Stack,
  CircularProgress,
  Typography,
} from '@mui/joy';
import { BuyEditProvider, useBuyEdit } from '@/contexts/BuyEditContext';
import { CreateEditBuyForm } from '@/components/Pages/Buys/Create/CreateEditBuyForm';
import { PrintBuyArticleLabelsButton } from '@/components/Pages/Buys/PrintBuyArticleLabelsModal';
import { OrderBuyResponseDTO } from '@/types';
import { PaymentModal } from '@/components/common/PaymentModal';
import { useLedgers } from '@/hooks/useLedgers';
import { useMemo } from 'react';
import { useOrderBuy, useCancelOrderBuy } from '@/hooks/useOrderBuys';
import { useAuth } from '@/contexts/AuthContext';

export const Route = createFileRoute('/_protected/buys/$id')({
  component: BuyDetail,
  head: ({ params }) => pageTitle(`Compras / ${params.id}`),
});

function BuyDetail() {
  const { id } = Route.useParams();
  const { hasClaim, isLoading: authLoading } = useAuth();
  const canRead = hasClaim('BUY_READ');
  const canWrite = hasClaim('BUY_WRITE');

  const { data: orderBuy, isLoading } = useOrderBuy(id);

  const { data: payments } = useLedgers({
    active: true,
    referenceId: orderBuy?.id,
    supplierId: orderBuy?.supplierId,
    enabled: !!orderBuy?.id,
  });

  const hasPayments = useMemo(() => {
    return !!(payments && payments.length > 0);
  }, [payments]);

  const totalPaid = useMemo(() => {
    return payments?.reduce((acc, payment) => acc + payment.amount, 0);
  }, [payments]);

  const { mutate: cancelOrderBuy, isPending: isCancellingOrderBuy } =
    useCancelOrderBuy();

  if (authLoading || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!canRead) {
    return <Navigate to='/buys' />;
  }

  if (!orderBuy) {
    return <div>Orden de compra no encontrada</div>;
  }

  const BuyDetailWithEditContext = ({
    orderBuy,
    hasPayments,
  }: {
    orderBuy: OrderBuyResponseDTO;
    hasPayments: boolean;
  }) => {
    const { isEdit, setIsEdit } = useBuyEdit();

    return (
      <>
        <Stack direction='row' justifyContent='space-between' mb={2}>
          <Breadcrumbs size='sm'>
            <Link to='/buys'>Compras</Link>
            <Typography>{id}</Typography>
          </Breadcrumbs>

          <Stack direction='row' gap={2}>
            {canWrite && (
              <>
                {isEdit ? (
                  <Button
                    variant='outlined'
                    color='neutral'
                    onClick={() => setIsEdit(false)}
                  >
                    Cancelar
                  </Button>
                ) : (
                  <Button
                    variant='outlined'
                    color='primary'
                    onClick={() => setIsEdit(true)}
                    disabled={orderBuy.status !== 'Confirmed' || hasPayments}
                  >
                    Editar
                  </Button>
                )}
                {orderBuy.status === 'Confirmed' && (
                  <Button
                    variant='outlined'
                    color='danger'
                    onClick={() => cancelOrderBuy(id)}
                    loading={isCancellingOrderBuy}
                    disabled={isEdit || hasPayments}
                  >
                    Cancelar
                  </Button>
                )}
                {orderBuy.status === 'Confirmed' && (
                  <PaymentModal
                    orderId={orderBuy.id}
                    isEdit={isEdit}
                    total={orderBuy.total}
                    totalPaid={totalPaid ?? 0}
                    supplierId={orderBuy.supplierId}
                  />
                )}
              </>
            )}
            <PrintBuyArticleLabelsButton details={orderBuy.details} />
          </Stack>
        </Stack>

        <CreateEditBuyForm orderBuy={orderBuy} />
      </>
    );
  };

  return (
    <BuyEditProvider>
      <BuyDetailWithEditContext orderBuy={orderBuy} hasPayments={hasPayments} />
    </BuyEditProvider>
  );
}
